import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Filter, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = [
    'all',
    'HVAC',
    'Solar',
    'EV Charging',
    'Smart Building',
    'Energy Storage',
    'Heat Recovery',
    'Building Envelope',
    'Power Generation'
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
        setFilteredProducts(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = products;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [searchTerm, selectedCategory, products]);

  return (
    <div data-testid="products-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 data-testid="products-title" className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6">
            OUR <span className="text-primary">PRODUCTS</span>
          </h1>
          <p data-testid="products-subtitle" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Complete product catalog from energy generation to intelligent automation
          </p>
        </motion.div>

        {/* Filters */}
        <div data-testid="product-filters" className="mb-12 space-y-6">
          {/* Search */}
          <div className="relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              placeholder="SEARCH PRODUCTS..."
              data-testid="product-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/50 border border-white/10 focus:border-primary focus:ring-1 focus:ring-primary h-12 pl-12 pr-4 font-mono text-sm text-foreground placeholder:text-muted-foreground outline-none"
            />
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 font-mono text-xs uppercase tracking-wider transition-all ${
                  selectedCategory === cat
                    ? 'bg-primary text-black font-bold'
                    : 'bg-transparent border border-primary/50 text-primary hover:bg-primary/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div data-testid="loading-spinner" className="text-center py-24">
            <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div data-testid="no-products-message" className="text-center py-24">
            <p className="text-muted-foreground text-lg">No products found matching your criteria.</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            data-testid="products-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
              >
                <Link
                  to={`/products/${product.slug}`}
                  data-testid={`product-card-${product.slug}`}
                  className="block group h-full"
                >
                  <div className="glass p-6 h-full flex flex-col hover:bg-white/5 transition-all hover:glow-green">
                    {/* Category Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono text-primary uppercase tracking-wider px-3 py-1 border border-primary/30">
                        {product.category}
                      </span>
                      <div className="w-8 h-8 border border-primary/30 flex items-center justify-center text-primary text-xs font-mono">
                        →
                      </div>
                    </div>

                    {/* Product Name */}
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                      {product.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-muted-foreground mb-4 flex-grow line-clamp-3">
                      {product.description}
                    </p>

                    {/* Stats */}
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground font-mono">ENERGY SAVINGS</span>
                        <span className="text-primary font-mono font-bold">{product.energy_savings}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground font-mono">EFFICIENCY</span>
                        <span className="text-primary font-mono font-bold">{product.efficiency}</span>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex items-center justify-between text-xs pt-4 border-t border-white/5">
                      <span className="text-primary font-mono uppercase">VIEW DETAILS</span>
                      <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-24 text-center glass p-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 uppercase">
            CAN'T FIND WHAT YOU'RE <span className="text-primary">LOOKING FOR?</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Contact our team for custom solutions tailored to your specific building requirements.
          </p>
          <Link
            to="/contact"
            data-testid="contact-for-custom-btn"
            className="inline-flex items-center px-8 py-4 bg-primary text-black font-mono uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-all hover:glow-green group"
          >
            CONTACT US TODAY
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
